import styles from '../../index.module.scss';
import type { WizardState } from 'components/EmployeeCost/SmartImport/wizardReducer';
import type { SmartImportChat } from '../useSmartImportChat';

const ADD_AS_NEW = '__add_as_new__';
const SKIP = '__skip__';

type EmployeeRow = WizardState['employeeRows'][number];

const statusLabel = (row: EmployeeRow): string => {
	if (row.skipped) return 'Skipped';
	switch (row.matchStatus) {
		case 'matched':
			return 'Matched';
		case 'saved':
			return 'Saved';
		case 'review':
			return `Review · ${row.matchScore}`;
		case 'new':
			return 'New';
		case 'blank_name':
			return 'Blank name';
		default:
			return '';
	}
};

export default function EmployeeMappingCard({ chat }: { chat: SmartImportChat }) {
	const { session, dispatch, roster } = chat;

	const rosterOptions = [...roster]
		.filter((e) => e.isActiveInApp)
		.map((e) => ({ value: e.id, label: e.fullName }))
		.concat(roster.filter((e) => !e.isActiveInApp).map((e) => ({ value: e.id, label: `${e.fullName} (inactive)` })));

	const selectValue = (row: EmployeeRow): string => {
		if (row.skipped) return SKIP;
		if (row.matchStatus === 'new') return ADD_AS_NEW;
		return row.matchedEmployeeId ?? '';
	};

	const handleChange = (row: EmployeeRow, value: string) => {
		if (value === ADD_AS_NEW) {
			dispatch({ type: 'EMPLOYEE_SET_ADD_AS_NEW', payload: { rowId: row.rowId } });
			return;
		}
		if (value === SKIP) {
			dispatch({ type: 'EMPLOYEE_SKIP_SET', payload: { rowId: row.rowId, skipped: true } });
			return;
		}
		if (value === '') return;
		const emp = roster.find((e) => e.id === value);
		dispatch({
			type: 'EMPLOYEE_MATCH_OVERRIDDEN',
			payload: {
				rowId: row.rowId,
				matchedEmployeeId: value,
				matchedEmployeeName: emp?.fullName ?? null,
				matchStatus: 'matched',
				inactive: emp?.isActiveInApp === false,
			},
		});
	};

	return (
		<div className={styles['action-card']}>
			<div className={styles['import-table-wrap']}>
				<table className={styles['import-table']}>
					<thead>
						<tr>
							<th>Employee (file)</th>
							<th>Status</th>
							<th>Maps to</th>
						</tr>
					</thead>
					<tbody>
						{session.employeeRows.map((row) => (
							<tr key={row.rowId}>
								<td>{row.csvName.trim() === '' ? <em>(blank name)</em> : <strong>{row.csvName}</strong>}</td>
								<td>{statusLabel(row)}</td>
								<td>
									<select value={selectValue(row)} onChange={(e) => handleChange(row, e.target.value)}>
										<option value="" disabled>
											Select employee…
										</option>
										<option value={ADD_AS_NEW}>Add as new employee</option>
										<option value={SKIP}>{row.skipped ? 'Undo skip' : 'Skip — do not import'}</option>
										{rosterOptions.map((o) => (
											<option key={o.value} value={o.value}>
												{o.label}
											</option>
										))}
									</select>
									{row.matchStatus === 'new' && !row.skipped && (
										<input
											className={styles['smart-import-card__name-input']}
											style={{ marginTop: 6, width: '100%' }}
											value={row.addAsNewName ?? row.csvName}
											placeholder="New employee name"
											onChange={(e) =>
												dispatch({
													type: 'EMPLOYEE_ADD_AS_NEW_NAME_SET',
													payload: { rowId: row.rowId, addAsNewName: e.target.value },
												})
											}
										/>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className={styles['smart-import-card__buttons-wrap']}>
				<button
					type="button"
					className={styles['action-card__confirm']}
					disabled={!chat.canAdvanceFromEmployees || chat.loading}
					onClick={() => void chat.continueFromEmployees()}
				>
					{chat.loading ? 'Loading…' : 'Continue'}
				</button>
			</div>
			{!chat.canAdvanceFromEmployees && (
				<div className={styles['action-card__status']}>
					Resolve every review/blank row (map, add as new, or skip) to continue.
				</div>
			)}
		</div>
	);
}
